// frontend/src/components/QrPaymentModal.js
import React, { useState } from 'react';
import api from '../api';
import { formatBobCurrency } from '../utilities/currency';

const QrPaymentModal = ({ course, onClose, onPaymentSuccess }) => {
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const qrImagePath = '/bnbqr.jpeg';
  const whatsappNumber = '59175563490';
  const whatsappMessage = encodeURIComponent(
    `Hola, adjunto mi comprobante de pago del curso "${course.title}".`
  );
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

  const handlePayment = async () => {
    try {
      setLoading(true);
      setError('');
    
      console.log('Iniciando proceso de pago...');
      
      // 1. Verificar estado del curso
      console.log('Verificando estado del curso...');
      const statusRes = await api.get(`/courses/${course.id}/status`);
      
      if (!statusRes.data.exists || statusRes.data.status !== 'published') {
        throw new Error('El curso no está disponible para inscripción');
      }
  
      // 2. Verificar inscripción existente
      console.log('Verificando inscripción existente...');
      const checkRes = await api.get(`/courses/${course.id}/enrollment`);
      
      if (checkRes.data?.isEnrolled) {
        console.log('Usuario ya inscrito, redirigiendo...');
        onPaymentSuccess();
        onClose();
        return;
      }
  
      // 3. Procesar pago
      console.log('Iniciando pago...');
      const response = await api.post('/payments/process', {
        courseId: course.id,
        method: 'qr_bnb'
      }, {
        timeout: 30000 // Timeout extendido para esta solicitud
      });
  
      if (!response.data.success) {
        throw new Error(response.data.message || 'Error en el pago');
      }
  
      console.log('Pago procesado con éxito');
      
      setPaymentConfirmed(true);
      console.log('Mostrando confirmación...');
    } catch (err) {
      console.error('Error completo en el pago:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
        stack: err.stack
      });
      
      let errorMessage = 'Error al procesar el pago';
      
      if (err.code === 'ECONNABORTED') {
        errorMessage = 'El servidor está tardando demasiado en responder. Por favor intenta nuevamente.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message.includes('disponible')) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmationClose = () => {
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    onPaymentSuccess();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Pagar con QR BNB</h2>
          <button 
            onClick={onClose} 
            className="text-gray-500 hover:text-gray-700"
            disabled={loading}
          >
            ✕
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 p-3 rounded mb-4">
            {error}
          </div>
        )}

        {!paymentConfirmed ? (
          <>
            <div className="mb-4">
              <p className="text-gray-700 mb-2">
                Curso: <span className="font-semibold">{course.title}</span>
              </p>
              <p className="text-gray-700 mb-2">
                Precio: <span className="font-semibold">{formatBobCurrency(course.price)}</span>
              </p>
            </div>

            <div className="bg-gray-100 p-4 rounded-lg mb-4 flex justify-center">
              <div className="w-full max-w-xs bg-white p-2 rounded">
                <img
                  src={qrImagePath}
                  alt="QR de pago BNB"
                  className="w-full h-auto rounded"
                />
              </div>
            </div>

            <p className="text-sm text-gray-500 mb-4">
              Escanea este código QR con la app de BNB Bolivia para completar tu pago.
            </p>

            <button
              onClick={handlePayment}
              disabled={loading}
              className={`w-full py-2 rounded text-white ${
                loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {loading ? 'Procesando pago...' : 'Confirmar pago'}
            </button>
          </>
        ) : (
          <div className="text-center py-8">
            <div className="text-green-500 text-5xl mb-4">✓</div>
            <h3 className="text-xl font-bold mb-2">Pago registrado</h3>
            <p className="text-gray-600 mb-2">Adjuntar comprobante de pago a los numeros de contacto.</p>
            <p className="text-sm text-gray-500 mb-6">Al continuar se abrira WhatsApp al +591 75563490.</p>
            <button
              onClick={handleConfirmationClose}
              className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              OK
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default QrPaymentModal;
